import { useCallback, useEffect, useState } from "react";
import { BarChart3, Loader2, RefreshCw } from "lucide-react";

import { cn } from "../../../lib/utils";
import { Button } from "../../ui/button";

const formatCount = (n) => {
  const num = Number(n) || 0;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return String(num);
};

const formatPercent = (rate) => `${((Number(rate) || 0) * 100).toFixed(1)}%`;

const HookPatternsPanel = ({ isDark, getHookPatterns, filters }) => {
  const [patterns, setPatterns] = useState([]);
  const [loading, setLoading] = useState(false);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getHookPatterns(filters);
      setPatterns(Array.isArray(res?.patterns) ? res.patterns : []);
    } catch (_e) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [getHookPatterns, filters]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const maxViews = Math.max(1, ...patterns.map((p) => p.avgViews));

  return (
    <div
      className={cn(
        "rounded-xl border",
        isDark ? "bg-white/[0.02] border-white/[0.06]" : "bg-white border-gray-200/70"
      )}
    >
      <div
        className={cn(
          "flex items-center justify-between px-4 py-3 border-b",
          isDark ? "border-white/[0.06]" : "border-gray-100"
        )}
      >
        <div className="flex items-center gap-2">
          <BarChart3 className={cn("h-4 w-4", isDark ? "text-purple-400" : "text-purple-500")} />
          <h2 className={cn("text-[14px] font-semibold", isDark ? "text-white" : "text-gray-900")}>
            Hook patronen
          </h2>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={refetch}
          className="h-7 gap-1 text-[12px]"
          disabled={loading}
        >
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          Vernieuwen
        </Button>
      </div>

      {loading && patterns.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className={cn("h-6 w-6 animate-spin", isDark ? "text-gray-500" : "text-gray-400")} />
        </div>
      ) : patterns.length === 0 ? (
        <div className={cn("text-center py-10 text-[13px]", isDark ? "text-gray-500" : "text-gray-500")}>
          Nog geen hook-data — voeg video's met een hook toe.
        </div>
      ) : (
        <div className="p-3 space-y-2">
          {patterns.map((p) => {
            const pct = (p.avgViews / maxViews) * 100;
            return (
              <div
                key={p.name}
                className={cn(
                  "rounded-lg border p-3",
                  isDark ? "bg-white/[0.02] border-white/[0.06]" : "bg-gray-50/50 border-gray-200/70"
                )}
              >
                <div className="flex items-center justify-between mb-2 gap-2">
                  <p
                    className={cn(
                      "text-[13px] font-semibold",
                      isDark ? "text-white" : "text-gray-900"
                    )}
                  >
                    {p.name}
                  </p>
                  <div className="flex items-center gap-3 text-[11px]">
                    <span className={cn("font-medium", isDark ? "text-gray-300" : "text-gray-700")}>
                      {p.count} hooks
                    </span>
                    <span className={cn("font-medium", isDark ? "text-blue-300" : "text-blue-700")}>
                      {formatCount(p.avgViews)} gem. views
                    </span>
                    <span className={cn("font-medium", isDark ? "text-emerald-300" : "text-emerald-700")}>
                      {formatPercent(p.avgEngagementRate)}
                    </span>
                  </div>
                </div>
                <div
                  className={cn(
                    "h-2 rounded-full overflow-hidden mb-2",
                    isDark ? "bg-white/[0.04]" : "bg-gray-100"
                  )}
                >
                  <div
                    className={cn("h-full transition-all", isDark ? "bg-purple-400/80" : "bg-purple-500/80")}
                    style={{ width: `${Math.max(2, pct)}%` }}
                  />
                </div>
                {p.examples?.length > 0 && (
                  <ul className="space-y-0.5">
                    {p.examples.slice(0, 3).map((ex, i) => (
                      <li
                        key={i}
                        className={cn(
                          "text-[11px] italic truncate",
                          isDark ? "text-gray-400" : "text-gray-600"
                        )}
                      >
                        “{ex}”
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default HookPatternsPanel;
